import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { usePortalNestProvider } from '@elfsight/widget-modal';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { createPortal } from 'react-dom';
import { usePopupContext } from '../../PopupProvider';
import { Price } from '../common';
import {
  Arrow,
  Close,
  Icons,
  Image,
  ImageComponentContainer,
  Nutrition
} from './popup-parts';

export function Popup() {
  const portal = usePortalNestProvider();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { popupElements = [] } = useSettings();
  const {
    popupVisible = false,
    popupItem,
    popupCurrentItemSection,
    popupSections,
    hidePopup = () => {},
    setPopupItem = () => {}
  } = usePopupContext();

  const [currentId, setCurrentId] = useState(0);
  const [items, setItems] = useState(popupCurrentItemSection);

  useEffect(() => {
    setItems(popupCurrentItemSection);
  }, [popupCurrentItemSection, popupSections, popupVisible]);

  useLayoutEffect(() => {
    if (popupVisible) {
      if (modalRef?.current) disableBodyScroll(modalRef.current);
    }

    return () => {
      if (modalRef?.current) enableBodyScroll(modalRef.current);
    };
  }, [popupVisible]);

  const close = () => {
    hidePopup();
    setPopupItem(undefined);
  };

  const setPrev = () => {
    if (currentId > 0) {
      setPopupItem(items[currentId - 1]);
    }

    if (currentId === 0) {
      setPopupItem(items[items.length - 1]);
    }
  };

  const setNext = () => {
    if (currentId !== items.length - 1) {
      setPopupItem(items[currentId + 1]);
    }

    if (currentId === items.length - 1) {
      setPopupItem(items[0]);
    }
  };

  useEffect(() => {
    if (items && popupItem) {
      setCurrentId(items.indexOf(popupItem));
    }
  }, [items, popupItem]);

  const showSection =
    popupElements.includes('section') &&
    !!popupSections?.length &&
    !!popupItem?.section &&
    popupItem?.section !== 'uncategorized';
  const showDescription =
    popupElements.includes('description') && !!popupItem?.description;
  const showPrice = popupElements.includes('price') && !!popupItem?.price;
  const showBadges =
    popupElements.includes('badges') && !!popupItem?.badges?.length;
  const showNutrition = popupElements.includes('nutrition');

  return createPortal(
    <Component ref={modalRef} _showPopup={popupVisible}>
      <Overlay onClick={close} />

      {popupVisible && (
        <Container>
          <Close onClick={close} />

          <ImageAndArrowsContainer>
            <Image image={popupItem?.image} alt="" />

            {items && items.length > 1 && (
              <>
                <ArrowLeft aria-label="Previous" onClick={setPrev}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                  >
                    <path d="M16.793 7.293L4.086 20l12.707 12.707 1.414-1.414L7.915 21H35v-2H7.915L18.207 8.707z" />
                  </svg>
                </ArrowLeft>

                <ArrowRight aria-label="Next" onClick={setNext}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                  >
                    <path d="M23.207 7.293L35.914 20 23.207 32.707l-1.414-1.414L32.085 21H5v-2h27.085L21.793 8.707z" />
                  </svg>
                </ArrowRight>
              </>
            )}
          </ImageAndArrowsContainer>

          <Info>
            {showSection && (
              <Section>
                {
                  (
                    popupSections?.find(
                      (item) => item.id === popupItem?.section
                    ) || {}
                  ).name
                }
              </Section>
            )}

            {(!!popupItem?.name || showPrice) && (
              <NameAndPrice>
                {!!popupItem?.name && <Name>{popupItem.name}</Name>}

                {showPrice && <StyledPrice price={popupItem!.price} />}
              </NameAndPrice>
            )}

            {showDescription && (
              <Description
                dangerouslySetInnerHTML={{
                  __html: popupItem!.description || ''
                }}
              />
            )}

            {showBadges && <Icons icons={popupItem!.badges} />}

            {showNutrition && <Nutrition data={popupItem} />}
          </Info>
        </Container>
      )}
    </Component>,
    portal || document.body
  );
}

const Component = styled.div<{ _showPopup?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  display: flex;
  justify-content: center;
  overflow: hidden auto;
  z-index: 99999;

  ${({ _showPopup }) =>
    _showPopup &&
    css`
      opacity: 1;
      pointer-events: all;
      visibility: visible;
    `}
`;
const Overlay = styled.div`
  position: fixed;
  background-color: rgba(17, 17, 17, 0.8);
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 999999;
`;
const Container = styled.div`
  position: absolute;
  top: 32px;
  max-width: 800px;
  width: calc(100% - 128px);
  background-color: #fff;
  border-radius: 16px;
  z-index: 999999;
  margin-bottom: 32px;

  ${({ theme: { windowWidth } }) =>
    windowWidth <= 608 &&
    css`
      top: 0;
      width: 100%;
      border-radius: 0;
      margin-bottom: 0;
      min-height: 100%;

      ${ImageComponentContainer} {
        border-radius: 0;
      }
    `};
`;
const ImageAndArrowsContainer = styled.div`
  position: relative;
`;

const ArrowLeft = styled(Arrow)`
  right: 100%;

  ${({ theme: { windowWidth } }) =>
    windowWidth <= 608 &&
    css`
      right: calc(100% - 52px);
    `};
`;
const ArrowRight = styled(Arrow)`
  left: 100%;

  ${({ theme: { windowWidth } }) =>
    windowWidth <= 608 &&
    css`
      left: calc(100% - 52px);
    `};
`;
const Info = styled.div`
  padding: 24px 16px;

  & > &:first-child {
    margin-top: 0;
  }
`;
const Section = styled.div`
  opacity: 0.65;
  font-size: 14px;
  line-height: 1.57;
  color: #111111;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const NameAndPrice = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
`;
const Name = styled.div`
  font-size: 24px;
  font-weight: bold;
  line-height: 1.17;
  color: #111111;
`;
const StyledPrice = styled(Price)`
  font-size: 20px;
  text-align: right;
  color: #111111;
`;
const Description = styled.div`
  margin-top: 12px;
  opacity: 0.65;
  font-size: 16px;
  line-height: 1.5;
  color: #111111;
  word-break: break-word;

  & a {
    ${focusVisibleMixin()}
  }
`;
